import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [myClinics, setMyClinics] = useState([]);
  const [activeClinic, setActiveClinic] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);

      // Load clinics for partner/superadmin
      if (['partner', 'superadmin'].includes(response.data.role)) {
        await loadMyClinics(token, response.data);
      }
    } catch (error) {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMyClinics = async (token, userData) => {
    try {
      // Use /clinics endpoint directly - it already has correct multi-tenant filtering
      const res = await axios.get(`${API_URL}/api/clinics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      const clinics = Array.isArray(data) ? data : (data.clinics || []);
      
      // Debug log for production troubleshooting
      if (clinics.length === 0 && userData?.clinic_id) {
        console.warn('loadMyClinics: 0 clinics from /api/clinics but user has clinic_id:', userData.clinic_id);
      }
      
      setMyClinics(clinics);

      // Set active clinic from user's clinic_id or first available
      const currentClinicId = userData?.clinic_id;
      if (currentClinicId) {
        const found = clinics.find(c => c.id === currentClinicId);
        if (found) {
          setActiveClinic(found);
        } else if (clinics.length > 0) {
          setActiveClinic(clinics[0]);
        }
      } else if (clinics.length > 0) {
        // Auto-select first clinic
        setActiveClinic(clinics[0]);
        // Also persist to backend
        await axios.post(`${API_URL}/api/auth/switch-clinic`,
          { clinic_id: clinics[0].id },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
    } catch (err) {
      console.error('Error loading clinics:', err);
    }
  };

  const switchClinic = useCallback(async (clinicId) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const res = await axios.post(`${API_URL}/api/auth/switch-clinic`,
        { clinic_id: clinicId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const found = myClinics.find(c => c.id === clinicId);
      if (found) setActiveClinic(found);

      // Update user's clinic_id locally
      setUser(prev => prev ? { ...prev, clinic_id: clinicId, clinic_name: res.data.clinic_name } : prev);

      // Reload page to refresh all data with new clinic context
      window.location.reload();
    } catch (err) {
      console.error('Switch clinic error:', err);
    }
  }, [myClinics]);

  const refreshClinics = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;
    await loadMyClinics(token, user);
  }, [user]);

  const login = async (email, password, directToken = null) => {
    let tokenToUse;
    let userData;

    if (directToken) {
      // Direct token login (impersonation)
      tokenToUse = directToken;
      localStorage.setItem('access_token', directToken);
      const meRes = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${directToken}` }
      });
      userData = { ...meRes.data, access_token: directToken };
    } else {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      userData = response.data;
      tokenToUse = response.data.access_token;
      if (tokenToUse) {
        localStorage.setItem('access_token', tokenToUse);
      }
    }

    setUser(userData);

    // Load clinics after login for partner/superadmin
    if (['partner', 'superadmin'].includes(userData.role)) {
      await loadMyClinics(tokenToUse, userData);
    }

    return userData;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      setUser(false);
      setMyClinics([]);
      setActiveClinic(null);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    logout,
    checkAuth,
    token: localStorage.getItem('access_token'),
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'partner',
    isSuperAdmin: user?.role === 'superadmin',
    isPartner: user?.role === 'partner',
    isReceptionist: user?.role === 'receptionist',
    isDoctor: user?.role === 'doctor',
    myClinics,
    activeClinic,
    switchClinic,
    refreshClinics
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
